"use client"

import type { StoreProductDto } from "@/generated/api/types.gen"
import type { Money } from "@/lib/money"
import { toPriceView } from "@/lib/money/price-view"
import { buildRoute, localizeHref } from "@/lib/routes"
import { wishlistGateway } from "@/platform/api/gateways/wishlist"
import { getRegion } from "@/platform/catalog/region-action"
import { useGateway } from "@/platform/session/use-api"
import { usePrivateQuery } from "@/platform/session/use-private-query"
import { useWishlistId } from "@/platform/shopper/use-wishlist-id"

export interface SavedProduct {
  id: string
  title: string
  image: string
  href: string
  price?: Money
  was?: Money
  payments?: Money
  installments?: number
  rating?: number
  reviewCount?: number
  discount?: number
  freeShipping?: boolean
}

// This endpoint leaves the typed rating fields null and populates only the legacy
// metadata copy, so every saved card rendered without its stars: the live payload
// carries rating: null alongside metadata.average_rating 4.49 over 232 responses.
const ratingOf = (product: StoreProductDto) => {
  if (typeof product.rating === "number" && typeof product.review_count === "number") {
    return { average: product.rating, count: product.review_count }
  }
  const { average_rating: average, count_responses: count } = product.metadata ?? {}
  if (typeof average !== "number" || typeof count !== "number") return null
  return { average, count }
}

export const toSaved = (product: StoreProductDto, countryCode: string): SavedProduct => {
  const view = toPriceView(product.price)
  const plan = view?.installment
  const rating = ratingOf(product)
  return {
    id: product.id,
    title: product.title,
    image: product.thumbnail ?? "",
    href: localizeHref(buildRoute.product(product.handle), countryCode),
    price: view?.amount,
    was: view?.compareAt ?? undefined,
    payments: plan?.amounts[0],
    installments: plan?.count,
    rating: rating?.average,
    reviewCount: rating?.count,
    discount: view?.isDiscounted ? view.savingsPercent : undefined,
    freeShipping: view?.freeShipping || undefined,
  }
}

export const WISHLIST_PRODUCTS = "wishlistProducts"

export const useWishlistProducts = (countryCode: string) => {
  const wishlistId = useWishlistId()
  const wishlists = useGateway(wishlistGateway)
  return usePrivateQuery<SavedProduct[]>(
    {
      queryKey: [WISHLIST_PRODUCTS, countryCode, wishlistId],
      refetchOnWindowFocus: false,
      enabled: !!wishlistId,
      queryFn: async () => {
        if (!wishlistId) return []
        const region = await getRegion(countryCode)
        if (!region?.id) throw new Error(`No region for ${countryCode}: saved products would come back unpriced`)
        return (await wishlists.products(wishlistId, region.id)).map((product) => toSaved(product, countryCode))
      },
    },
    true
  )
}
